import { QueryClientProvider, useQuery } from '@tanstack/react-query'
import { AuthProvider, RequireAuth, useAuth } from '@milliswift/auth'
import {
  createQueryClient,
  installMocks,
  notificationQueries,
  queryKeys,
  useWebSocket,
  type PushFrame,
} from '@milliswift/api-client'
import { AppShell, ErrorBoundary, PageSkeleton, ToastProvider } from '@milliswift/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Bell, Database, KeyRound, LayoutDashboard, Monitor, ShieldCheck, User } from 'lucide-react'
import { Navigate, Route, Routes } from 'react-router-dom'

import ApiKeys from './pages/ApiKeys'
import Databases from './pages/Databases'
import Devices from './pages/Devices'
import Notifications from './pages/Notifications'
import Overview from './pages/Overview'
import Profile from './pages/Profile'
import Security from './pages/Security'

const queryClient = createQueryClient()
installMocks()

/**
 * Account-scoped navigation. The shell's account variant drops the redundant
 * "Manage Account" menu entry because these pages ARE that destination.
 */
const NAV = [
  { label: 'Overview', href: '/overview', icon: LayoutDashboard, matchPrefix: false },
  { label: 'Profile', href: '/profile', icon: User, matchPrefix: false },
  { label: 'Security', href: '/security', icon: ShieldCheck, matchPrefix: false },
  { label: 'API Keys', href: '/api-keys', icon: KeyRound, matchPrefix: false },
  { label: 'Devices', href: '/devices', icon: Monitor, matchPrefix: false },
  { label: 'Databases', href: '/databases', icon: Database, matchPrefix: false },
  { label: 'Notifications', href: '/notifications', icon: Bell, matchPrefix: false },
]

/**
 * The header bell and the devices page both want to refresh the moment
 * something happens. The push channel may not be reachable, so its `degraded`
 * mode is not an error — the queries keep their own polling intervals as the
 * fallback, and a working socket simply makes updates arrive sooner.
 */
function LiveUpdates() {
  const queryClient = useQueryClient()

  useWebSocket('/ws', {
    onMessage: (frame: PushFrame) => {
      // The platform's frames are { event, data } — see PushFrame.
      if (frame.event.includes('device')) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.devices })
      }
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  return null
}

function Shell() {
  const { logout } = useAuth()
  const unread = useQuery(notificationQueries.unreadCount())

  return (
    <AppShell
      variant="account"
      nav={NAV}
      notificationCount={unread.data?.unread_count ?? 0}
      onNotificationsClick={() => (window.location.href = '/notifications')}
      onSignOut={async () => {
        await logout()
        window.location.href = '/login'
      }}
    >
      <LiveUpdates />
      <Routes>
        <Route path="/overview" element={<Overview />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/security" element={<Security />} />
        <Route path="/api-keys" element={<ApiKeys />} />
        <Route path="/devices" element={<Devices />} />
        <Route path="/databases" element={<Databases />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/" element={<Navigate to="/overview" replace />} />
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
    </AppShell>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider redirectOnUnauthorized={false}>
            <RequireAuth loading={<PageSkeleton />}>
              <Shell />
            </RequireAuth>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
